import { css } from 'lit';
import { connect } from '@/utils/connect';
import { customElement, state } from 'lit/decorators.js';
import store, { RootState } from '@/redux/redux-store';
import { CSSResultArray, LitElement, html, nothing } from 'lit';
import { AppAlert } from '@typings/shared-types';
import { UserPermissions } from '@typings/api';
import selectAlerts from '@/redux/alert-selectors';
import { resendSignMessage } from '@/api/dmsMessageApi';
import { getSigns } from '@/redux/dms/dms-actions-sign';
import userHasPermission from '@/redux/user-permissions';
import formatRelativeDate from '@/utils/FormatRelativeDate';
import { isValidNumber } from '@/utils/utils';
import resetScss from '@css/lib/reset.scss';
import sharedScss from '@css/shared.scss';
import spinnerCss from '@css/spinner.css';
import './app-icon';
import './app-btn';
import { classMap } from 'lit/directives/class-map.js';
import bind from 'bind-decorator';
import { Ref, createRef, ref } from 'lit/directives/ref.js';
import containsTargetPenetrate from '@/utils/contains-penetrate';
import { ifDefined } from 'lit/directives/if-defined.js';
import utilityClassesCss from '@css/utilityClasses.css';
import alertsPanelCss from './alerts-panel.css';

@customElement('alerts-panel')
export default class AlertsPanel extends connect(store)(LitElement) {
	@state() private alerts: AppAlert[] = [];

	@state() private panelOpen = false;

	@state() private resendingAll = false;

	@state() private userCanResendMessages = userHasPermission(UserPermissions.DMS_CAN_MANAGE_QUEUE);

	private panelRef: Ref<HTMLElement> = createRef();

	private toggleRef: Ref<HTMLElement> = createRef();

	public static override get styles(): CSSResultArray {
		return [resetScss, sharedScss, utilityClassesCss, spinnerCss, alertsPanelCss];
	}

	private get resendableSignIds(): number[] {
		return [
			...new Set(
				this.alerts
					.map((alert) => alert.signId)
					.filter((signId): signId is number => isValidNumber(signId)),
			),
		];
	}

	@bind
	private async onClickResendAll(): Promise<void> {
		const signIds = this.resendableSignIds;
		if (signIds.length === 0 || !this.userCanResendMessages || this.resendingAll) {
			return;
		}

		this.resendingAll = true;

		// TODO: replace per-sign resend loop with a single bulk resend API call once available
		await Promise.all(signIds.map((signId) => resendSignMessage(signId)));
		await store.dispatch(getSigns());

		this.resendingAll = false;
	}

	panelTemplate() {
		if (!this.panelOpen) {
			return nothing;
		}

		const showResendAll = this.userCanResendMessages && this.resendableSignIds.length > 0;

		return html`<div ${ref(this.panelRef)} class="panel">
			<div class="panel-title">
				Active alerts
				<span class="pill pill-danger">${this.alerts.length} unread</span>
				${showResendAll
					? html`<app-btn
							icon="img/sprites.svg#icon-refresh"
							@clicked=${this.onClickResendAll}
							?disabled=${this.resendingAll}
							>Resend all DMS messages</app-btn
						>`
					: nothing}
			</div>
			<div class="panel-content">
				${this.resendingAll
					? html`<div class="flex align-items-center justify-content-center p-sm">
							<div class="spinner blue small"></div>
						</div>`
					: nothing}
				${this.alerts.length === 0 && !this.resendingAll
					? html`<div class="flex align-items-center justify-content-center p-sm">
							<span class="status">No unread alerts</span>
						</div>`
					: nothing}
				${this.resendingAll
					? nothing
					: this.alerts.map((alert) => {
							const danger = alert.category === 'ERROR' || alert.category === 'CRITICAL';
							const caution = alert.category === 'WARNING';
							return html`<div
								class="${classMap({
									flex: true,
									alert: true,
									gap: true,
									danger,
									caution,
								})}"
							>
								<app-icon
									width="36"
									height="36"
									href="${alert.icon}"
									class="${classMap({
										'alert-icon': true,
										'danger': danger,
										'caution': caution,
									})}"
								></app-icon>
								<div
									class="${classMap({
										'flex': true,
										'col': true,
										'gap-half': true,
										danger,
										caution,
									})}"
								>
									<h3>
										${alert.title}
										<span
											class="${classMap({
												'badge': true,
												'badge-danger': danger,
												'badge-caution': caution,
											})}"
											>${alert.category}</span
										>
									</h3>
									<p>${alert.content}</p>
									<span class="status"
										>${alert.source} - ${formatRelativeDate(alert.timeInMillis)}</span
									>
									<div class="flex gap flex-wrap-wrap">
										${alert.actions.map(
											(action) =>
												html`<app-btn
													icon=${ifDefined(action.icon)}
													@click=${action.callbackFn}
													.link=${action.link}
													>${action.actionTitle}</app-btn
												>`,
										)}
									</div>
								</div>
							</div>`;
						})}
			</div>
		</div>`;
	}

	togglePanelOpen() {
		this.panelOpen = !this.panelOpen;
	}

	override render() {
		return html`<div class="alerts-container">
				<app-icon
					${ref(this.toggleRef)}
					width="32"
					height="32"
					href="img/sprites.svg#bell-icon"
					@click=${this.togglePanelOpen}
					class="${classMap({ open: this.panelOpen, active: this.alerts.length > 0 })}"
					><app-bubble count=${this.alerts.length}></app-bubble
				></app-icon>
			</div>
			${this.panelTemplate()}`;
	}

	override stateChanged(state: RootState) {
		this.alerts = selectAlerts(state);
		this.userCanResendMessages = userHasPermission(UserPermissions.DMS_CAN_MANAGE_QUEUE);
	}

	@bind
	private onGlobalMouseup(e: MouseEvent): void {
		const panelElement = this.panelRef.value;
		const toggleElement = this.toggleRef.value;

		if (
			panelElement != null &&
			!containsTargetPenetrate(panelElement, e) &&
			toggleElement != null &&
			!containsTargetPenetrate(toggleElement, e)
		) {
			this.panelOpen = false;
		}
	}

	override connectedCallback(): void {
		window.addEventListener('mouseup', this.onGlobalMouseup);

		super.connectedCallback();
	}

	override disconnectedCallback(): void {
		window.removeEventListener('mouseup', this.onGlobalMouseup);

		super.disconnectedCallback();
	}
}
